import { ButtonHTMLAttributes, forwardRef } from "react";
import clsx from "clsx";
import { Loader2 } from "lucide-react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Stato di caricamento: disabilita il bottone e mostra uno spinner.
   * Va sempre gestito con `finally { setLoading(false) }` nel form chiamante,
   * per evitare che il pulsante resti bloccato in caso di errore
   * (regola UX critica del documento di specifica).
   */
  loading?: boolean;
}

// Ombra + lieve sollevamento su hover per primary/secondary/danger, la
// stessa "fisicità" già usata per le tessere di sezione (sectionColors.ts)
// applicata qui ai bottoni generici — ghost resta piatto di proposito,
// è la variante a minor enfasi e non deve competere visivamente.
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-white shadow-card hover:bg-primary-light hover:shadow-lift hover:-translate-y-0.5 active:translate-y-0 active:shadow-card disabled:bg-primary/50 disabled:shadow-none disabled:translate-y-0",
  secondary:
    "bg-white text-primary border border-line shadow-card hover:bg-primary/5 hover:border-primary/30 hover:shadow-lift hover:-translate-y-0.5 active:translate-y-0 active:shadow-card disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none",
  danger:
    "bg-accent text-white shadow-card hover:bg-accent-dark hover:shadow-lift hover:-translate-y-0.5 active:translate-y-0 active:shadow-card disabled:bg-accent/50 disabled:shadow-none disabled:translate-y-0",
  ghost: "bg-transparent text-ink hover:bg-black/5 disabled:opacity-50",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", size = "md", loading = false, disabled, className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        className={clsx(
          "inline-flex items-center justify-center rounded-lg font-semibold",
          "transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out",
          "disabled:cursor-not-allowed",
          VARIANT_CLASSES[variant],
          SIZE_CLASSES[size],
          className
        )}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";
